import { Dispatch, RefObject, SetStateAction, useEffect, useRef } from "react";
import toast from "react-hot-toast";

import { Comment } from "@/api";
import { AdminTag, Button, UserAvatar } from "@/components";
import { useAppProperties } from "@/hooks";
import { Reply, ThreadIcon } from "@/icons";
import { useTranslate, useUser } from "@/providers/context";
import { cn } from "@/utils";

import { useUpdateComment } from "../hooks/useUpdateComment";
import { useCommentsContext } from "../providers/context/useCommentsContext";
import { CommentDropdown } from "./CommentDropdown";
import { CommentInput } from "./CommentInput";

interface SingleCommentProps {
  comment: Comment;
  setReplying: (replying: boolean) => void;
  setShowReplies: Dispatch<SetStateAction<boolean>>;
  topLevel?: boolean;
  lastReply?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
}

const makeLinksClickable = (text: string) => {
  const urlRegex = /(?:https?:\/\/)?(?:www\.)?[^\s]+\.[^\s]+/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const url = match[0].startsWith("www.") ? `https://${match[0]}` : match[0];
    parts.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:text-item-secondary"
      >
        {match[0]}
      </a>,
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

export const SingleComment = ({
  comment,
  setReplying,
  setShowReplies,
  topLevel,
  lastReply,
  inputRef,
}: SingleCommentProps) => {
  const { translate, formatRelativeDateFromString } = useTranslate();
  const { data: properties } = useAppProperties();
  const { isAdminUser } = useUser();

  const { commentToEdit, setCommentToEdit, sourceId, itemId } =
    useCommentsContext();
  const editCommentRef = useRef<HTMLInputElement>(null);
  const { mutate: updateComment, isLoading: isUpdating } = useUpdateComment();

  const allowLinks = properties?.AllowLinksInComments === "1";
  const isPending = comment.SourceCommentId === 0; // While the comment is being created
  const isEditing = commentToEdit?.SourceCommentId === comment.SourceCommentId;

  useEffect(() => {
    if (isEditing) {
      editCommentRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <div
      key={comment.SourceCommentId!}
      className={cn(
        "relative flex flex-col text-item-primary",
        !topLevel && "border-l-[2px] border-transparent pl-7",
        !topLevel && !lastReply && "border-border",
        isPending && "opacity-60",
      )}
    >
      <div className="flex items-center gap-3">
        <UserAvatar name={comment.Name} imageUrl={comment.Thumbnail} />
        <div className="flex w-full flex-col justify-start">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="p2-semibold">{comment.Name}</h4>
              {isAdminUser(comment.SourceCustomerId ?? "") && <AdminTag />}
            </div>
            {!isPending && <CommentDropdown comment={comment} />}
          </div>
          <div className="caption flex items-center gap-1 pt-[1px] text-item-secondary">
            <p>{formatRelativeDateFromString(comment.CommentDate)}</p>
            {comment.EditedAt && (
              <>
                <div>•</div>
                <div>{translate("Edited")}</div>
              </>
            )}
          </div>
        </div>
      </div>
      {isEditing ? (
        <CommentInput
          isEditing
          initialValue={comment.Comment}
          inputRef={editCommentRef}
          isSaving={isUpdating}
          putComment={(c) => {
            updateComment(
              {
                Comment: c,
                SourceCommentId: comment.SourceCommentId,
                SourceItemId: sourceId,
                itemId,
              },
              {
                onSuccess: () => setCommentToEdit(undefined),
                onError: () => {
                  toast(
                    translate("Something went wrong, please try again later."),
                  );
                  setCommentToEdit(undefined);
                },
              },
            );
          }}
          onCancel={() => setCommentToEdit(undefined)}
        />
      ) : (
        <p className="p1-regular break-words pt-2">
          {allowLinks ? makeLinksClickable(comment.Comment) : comment.Comment}
        </p>
      )}
      {topLevel ? (
        <div className="mt-7 flex">
          <div className="caption flex w-full justify-between ">
            <Button
              variant="tertiary"
              size="s"
              className="flex gap-1 uppercase text-item-secondary"
              onClick={() => {
                setShowReplies(true); // Show replies when replying
                setReplying(true);
                setTimeout(() => {
                  inputRef?.current?.focus();
                }, 50);
              }}
            >
              <Reply /> {translate("Reply")}
            </Button>
            {comment.Responses.length > 0 && (
              <Button
                variant="tertiary"
                size="s"
                className="text-item-secondary"
                onClick={() =>
                  setShowReplies((current) => {
                    if (current) {
                      setReplying(false);
                    }
                    return !current;
                  })
                }
              >
                {comment.Responses.length} {translate("replies")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>{!lastReply && <div className="mt-10" />}</>
      )}
      {!topLevel && (
        <div className="absolute left-[-2px] top-0 text-border">
          <ThreadIcon className="h-[20px] w-[16px]" />
        </div>
      )}
    </div>
  );
};
